import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  SmartToy as BotIcon,
  Add as AddIcon,
  StorefrontOutlined as MarketplaceIcon,
} from "@mui/icons-material";

import { ReactComponent as LogoIcon } from "../assets/images/logo.svg";
import MessageBell from "./messages/MessageBell";
import ProfileMenu from "./nav/ProfileMenu";
import TopBarSearch from "./nav/TopBarSearch";
import ImpersonationBanner from "./common/ImpersonationBanner";
import { useAuth } from "../contexts/AuthContext";
import { getMarketplaceStatus } from "../services/PartnershipService";
import { layout } from "../theme";

// Shared bordered "icon-btn" style for the top bar action icons.
const iconBtnSx = {
  width: 34,
  height: 34,
  border: "1px solid",
  borderColor: "neutral.100",
  backgroundColor: "transparent",
  color: "text.secondary",
  "&:hover": {
    borderColor: "neutral.400",
    color: "text.primary",
    backgroundColor: "background.default",
  },
};

// Persistent top bar across all breakpoints.
// Note: useMenu() menuText / showBack are no longer rendered here — pages still
// call menuInit(...) (harmless dead writes). Repurpose later as breadcrumbs.
const TopNav = ({ changeIsMenuOpen, onChatOpen }) => {
  const navigate = useNavigate();
  // `?? {}` guards the same undefined-context case AdminChatbot's gate documents: this can render
  // before the auth context is available, and destructuring undefined takes the page down.
  const { isSuperAdmin, isCompanyAdmin, isTrainer, isCompanyGroupAdmin } = useAuth() ?? {};
  // Both the super-admin and the company group admin are company-less personas: the company-scoped
  // top-bar affordances (client search, "new client" CTA) have no company to act on, so they're
  // hidden for both. A group admin only views its companies' stats or impersonates.
  const isGroupAdmin = isCompanyGroupAdmin?.();
  const isCompanyless = isSuperAdmin?.() || isGroupAdmin;
  const [marketplaceOpen, setMarketplaceOpen] = useState(false);

  useEffect(() => {
    // admin/marketplace/status is company-scoped (ADMIN/TRAINER). Skip it for BOTH company-less
    // personas: a super-admin sees the marketplace entry regardless, and a group admin fails every
    // branch of showMarketplace below, so the response is discarded either way. A group admin isn't
    // on the /admin/** floor (SecurityConfig: SYSTEM_ADMIN|ADMIN|TRAINER), so the call was a
    // guaranteed rejection — the same guard SideNav already applies.
    if (isCompanyless) return;
    getMarketplaceStatus()
      .then((res) => setMarketplaceOpen(!!res.data?.open))
      .catch(() => {});
  }, []);

  // SYSTEM_ADMIN always sees the entry; others only when the marketplace is open.
  const showMarketplace =
    isSuperAdmin?.() || ((isTrainer?.() || isCompanyAdmin?.()) && marketplaceOpen);

  return (
    <AppBar
      position="fixed"
      sx={{
        height: layout.topBarHeight,
        zIndex: layout.zIndex.topBar,
        top: `${layout.envBannerHeight}px`,
        left: 0,
        right: 0,
        backgroundColor: "background.paper",
        borderBottom: (theme) => `1px solid ${theme.palette.neutral[100]}`,
        boxShadow: (theme) => theme.customShadows?.topbar || "none",
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: layout.topBarHeight + "px !important",
          height: layout.topBarHeight,
          px: { xs: 2, md: 3 },
          gap: { xs: 1, md: 1.75 },
        }}
      >
        {/* Brand wordmark + logo (visually right in RTL) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
            paddingInlineEnd: "18px",
            borderInlineEnd: (theme) => `1.5px solid ${theme.palette.neutral[100]}`,
            "& svg": { height: 24, width: "auto" },
          }}
        >
          <LogoIcon />
        </Box>

        {/* Mobile hamburger */}
        <IconButton
          aria-label="menu"
          onClick={changeIsMenuOpen}
          sx={{
            display: { xs: "inline-flex", md: "none" },
            color: "text.secondary",
            width: 34,
            height: 34,
          }}
        >
          <MenuIcon sx={{ fontSize: 18 }} />
        </IconButton>

        {/* Search — searches company-scoped entities (clients/templates/exercises/…),
            so it's hidden for the company-less personas (super-admin, company group admin). */}
        {!isCompanyless && (
          <TopBarSearch
            sx={{
              display: { xs: "none", sm: "flex" },
              mx: { sm: 1, md: 2 },
            }}
          />
        )}

        {/* Spacer pushes the rest to the visual-left edge. It also hosts the impersonation pill —
            centered in the otherwise-empty middle of the bar, where it's obvious without floating
            over page content the way the old fixed bottom bar did. Renders nothing when not
            impersonating, leaving a plain spacer. */}
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center", minWidth: 0 }}>
          <ImpersonationBanner />
        </Box>

        {/* New trainee CTA — company-scoped creation, so hidden for the company-less personas
            (super-admin, company group admin). A group admin has no creation capabilities. */}
        {!isCompanyless && (
          <>
            <Box
              component="button"
              onClick={() => navigate("/app/clients/new")}
              sx={{
                display: { xs: "none", sm: "inline-flex" },
                alignItems: "center",
                gap: "5px",
                background: "#01BBA7",
                color: "#fff",
                height: 34,
                px: "15px",
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: 600,
                letterSpacing: "-0.1px",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all .14s",
                "&:hover": {
                  background: "#019385",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(1,187,167,.35)",
                },
              }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
              לקוח חדש
            </Box>
            <IconButton
              onClick={() => navigate("/app/clients/new")}
              aria-label="לקוח חדש"
              sx={{
                display: { xs: "inline-flex", sm: "none" },
                width: 34,
                height: 34,
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                "&:hover": { backgroundColor: "primary.dark" },
              }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </>
        )}

        {/* Marketplace */}
        {showMarketplace && (
          <Tooltip title="מרקטפלייס">
            <IconButton onClick={() => navigate("/app/marketplace")} sx={iconBtnSx}>
              <MarketplaceIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Messages — the staff inbox. Hidden for a group admin only (NOT for the super-admin, who
            is on the /admin/admin-message floor and keeps today's bell): broadcast receipts are
            created per company for users holding ADMIN or TRAINER in that company
            (AdminBroadcastService.send), so a group admin can never be a recipient — the badge would
            always read 0 and opening the drawer would fire a rejected GET /admin/admin-message/inbox. */}
        {!isGroupAdmin && <MessageBell />}

        {/* AI chat — the assistant answers questions about company-scoped data (clients,
            programs, nutrition), so it has nothing to work with for the company-less personas
            (super-admin, company group admin). Hidden for them, same as search and the new-client
            CTA above. */}
        {!isCompanyless && (
          <Tooltip title="עוזר AI">
            <IconButton onClick={onChatOpen} sx={iconBtnSx}>
              <BotIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Profile menu */}
        <ProfileMenu />
      </Toolbar>
    </AppBar>
  );
};

export default TopNav;
